import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, ChevronRight } from 'lucide-react';
import { AnimatedSection } from '../components/AnimatedSection';
import { CTASection } from '../components/CTASection';
import { BlogCard, FAQAccordion } from '../components/Cards';
import { blogs } from '../data/mockData';
export function BlogPost() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  // Find blog or use first one as fallback
  const blog = blogs.find(b => b.slug === slug) || blogs[0];
  // Get related blogs
  const relatedBlogs = blogs.filter(b => b.id !== blog.id).slice(0, 3);
  return <div className="w-full bg-white pt-24" data-id="element-348">
      {/* Breadcrumbs */}
      <div className="max-w-[800px] mx-auto px-6 lg:px-0 py-8" data-id="element-349">
        <Link to="/kennisbank" className="inline-flex items-center text-sm text-charcoal/60 hover:text-accent transition-colors mb-8" data-id="element-350">
          <ArrowLeft size={16} className="mr-2" data-id="element-351" /> Terug naar kennisbank
        </Link>
        <div className="flex items-center gap-2 text-xs text-charcoal/50 uppercase tracking-wider font-medium mb-6" data-id="element-352">
          <Link to="/kennisbank" className="hover:text-accent" data-id="element-353">
            Kennisbank
          </Link>
          <ChevronRight size={12} data-id="element-354" />
          <Link to={`/kennisbank/${blog.cluster}`} className="hover:text-accent" data-id="element-355">
            {blog.cluster.replace('-', ' ')}
          </Link>
        </div>

        <AnimatedSection data-id="element-356">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-light text-charcoal leading-tight tracking-tight mb-6" data-id="element-357">
            {blog.title}
          </h1>
          <div className="flex items-center gap-6 text-sm text-charcoal/60 mb-12 border-b border-warmgray/50 pb-8" data-id="element-358">
            <span className="flex items-center gap-2" data-id="element-359">
              <Calendar size={16} data-id="element-360" /> {blog.date}
            </span>
            <span className="flex items-center gap-2" data-id="element-361">
              <Clock size={16} data-id="element-362" /> {blog.readTime} leestijd
            </span>
          </div>
        </AnimatedSection>
      </div>

      {/* Featured Image */}
      <div className="max-w-[1000px] mx-auto px-6 lg:px-0 mb-16" data-id="element-363">
        <AnimatedSection delay={0.2} data-id="element-364">
          <img src={blog.image} alt={blog.title} className="w-full h-[400px] md:h-[500px] object-cover rounded-2xl shadow-sm" data-id="element-365" />
        </AnimatedSection>
      </div>

      {/* Content */}
      <div className="max-w-[800px] mx-auto px-6 lg:px-0 mb-24" data-id="element-366">
        <AnimatedSection delay={0.3} className="prose prose-lg prose-headings:font-light prose-headings:text-charcoal prose-p:text-charcoal/80 prose-a:text-accent hover:prose-a:text-accent-hover" data-id="element-367">
          <p className="lead text-xl text-charcoal font-medium mb-8" data-id="element-368">
            {blog.excerpt}
          </p>

          <h2 data-id="element-369">Waarom is dit belangrijk?</h2>
          <p data-id="element-370">
            Een bedrijfswaardering is vaak een complex proces waarbij emotie en
            ratio door elkaar lopen. Zeker bij een verkoop of overname is het
            cruciaal om te weten waar u aan toe bent. De waarde van een
            onderneming wordt niet alleen bepaald door de winst van vandaag,
            maar vooral door de verwachte kasstromen in de toekomst.
          </p>

          <h2 data-id="element-371">Welke methoden worden gebruikt?</h2>
          <p data-id="element-372">
            In de praktijk zien we vaak een combinatie van methoden. De
            Discounted Cash Flow (DCF) methode kijkt naar toekomstige kasstromen
            en wordt academisch als het meest zuiver beschouwd. Daarnaast worden
            EBITDA multiples gebruikt als marktconforme referentie. Een
            professionele{' '}
            <Link to="/bedrijfswaardering" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-373">
              bedrijfswaardering
            </Link>{' '}
            weegt deze methoden zorgvuldig af tegen uw specifieke situatie.
          </p>

          <h2 data-id="element-374">Het belang van normalisatie</h2>
          <p data-id="element-375">
            Normalisatie is een cruciale stap. Hierbij corrigeren we de
            historische cijfers voor incidentele posten, zoals een eenmalige
            juridische claim of een niet-marktconforme management fee. Dit geeft
            een eerlijker beeld van de werkelijke verdiencapaciteit van de
            onderneming, wat direct invloed heeft op de uiteindelijke waarde.
          </p>

          <h2 data-id="element-376">Wanneer heeft u een waardering nodig?</h2>
          <p data-id="element-377">
            Of u nu denkt aan verkoop, een bedrijfsoverdracht binnen de familie,
            of te maken heeft met een aandeelhoudersgeschil: een objectief
            rapport biedt houvast. Bekijk onze{' '}
            <Link to="/werkwijze" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-378">
              werkwijze
            </Link>{' '}
            om te zien hoe wij dit proces gestructureerd aanpakken.
          </p>

          <div className="bg-offwhite p-8 rounded-xl my-10 border-l-4 border-accent" data-id="element-379">
            <h4 className="mt-0" data-id="element-380">Tip van de expert</h4>
            <p className="mb-0 text-sm" data-id="element-381">
              Begin minimaal 3 tot 5 jaar voor een geplande verkoop met het
              optimaliseren van uw bedrijfswaarde. Dit geeft u de tijd om
              afhankelijkheden te verminderen en de winstgevendheid te
              verbeteren.
            </p>
          </div>

          <h2 data-id="element-382">Conclusie</h2>
          <p data-id="element-383">
            Het bepalen van de juiste waarde is een samenspel van financiële
            theorie en marktpraktijk. Zorg dat u goed voorbereid bent en schakel
            tijdig een specialist in.
          </p>
        </AnimatedSection>

        {/* FAQ Section */}
        <AnimatedSection delay={0.4} className="mt-16 mb-16" data-id="element-384">
          <h2 className="text-3xl font-light text-charcoal mb-8" data-id="element-385">
            Veelgestelde vragen
          </h2>
          <div className="bg-white rounded-2xl p-6 md:p-10 shadow-sm border border-warmgray/50" data-id="element-386">
            <FAQAccordion question="Hoe lang duurt een bedrijfswaardering?" answer="Een standaard waarderingstraject neemt gemiddeld 3 tot 5 weken in beslag, gerekend vanaf het moment dat alle benodigde financiële informatie is aangeleverd." data-id="element-387" />
            <FAQAccordion question="Wat kost een professionele waardering?" answer="De kosten variëren afhankelijk van de complexiteit van de onderneming en het doel van de waardering. Voor een MKB-bedrijf liggen de kosten vaak tussen de €2.500 en €5.000 voor een indicatieve waardering." data-id="element-388" />
            <FAQAccordion question="Welke documenten heb ik nodig?" answer="Meestal hebben we de jaarrekeningen van de afgelopen 3 tot 5 jaar nodig, actuele tussentijdse cijfers, een prognose voor de komende jaren, en inzicht in eventuele uitzonderlijke baten of lasten." data-id="element-389" />
          </div>
        </AnimatedSection>

        {/* Internal Link Block */}
        <AnimatedSection className="mt-16 p-8 bg-charcoal text-white rounded-2xl text-center" data-id="element-390">
          <h3 className="text-2xl font-light mb-4" data-id="element-391">
            Hulp nodig bij uw specifieke situatie?
          </h3>
          <p className="text-warmgray/80 mb-6" data-id="element-392">
            Ontdek hoe wij u kunnen helpen met een professionele
            bedrijfswaardering.
          </p>
          <Link to="/bedrijfswaardering" className="inline-block bg-accent hover:bg-accent-hover text-white px-6 py-3 rounded-full text-sm font-medium transition-all" data-id="element-393">
            Bekijk onze diensten
          </Link>
        </AnimatedSection>
      </div>

      {/* Related Articles */}
      <section className="py-24 bg-offwhite" data-id="element-394">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12" data-id="element-395">
          <AnimatedSection className="mb-12" data-id="element-396">
            <h2 className="text-3xl font-light text-charcoal" data-id="element-397">
              Gerelateerde artikelen
            </h2>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-398">
            {relatedBlogs.map((b, index) => <AnimatedSection key={b.id} delay={index * 0.1} data-id="element-399">
                <BlogCard {...b} data-id="element-400" />
              </AnimatedSection>)}
          </div>
        </div>
      </section>
    </div>;
}